import { Messages } from 'config';
import { useAppSelector } from 'store';
import { Flex } from 'components/flex';
import { formatDate } from 'utils/date';
import { mapQueries } from 'utils/query';
import { capitalize } from 'utils/string';
import { formatPrice } from 'utils/helpers';
import { ListCard } from 'components/list-card';
import { ListHeader } from 'components/list-header';
import { DeleteDialog } from 'components/delete-dialog';
import { Feature, PayoutType, PayoutStatus } from 'types';
import { PayoutStatusBadge } from 'components/payout-status-badge';
import { selectAuthUser, isAuthCompanyUser } from 'store/slices/auth';
import { ApprovalPayoutDialog } from 'components/approval-payout-dialog';
import { Payout, useListQuery, useDeleteMutation } from 'store/api/payout';
import { CreateBonusAdvanceDialog } from 'components/create-bonus-advance-dialog';
import {
  isMobile,
  useFilter,
  useShowMore,
  handleMutation,
  useSearchQuery,
} from 'hooks';

import * as CommonStyles from '../styles';

import { BonusesProps, CommonListProps } from './types';

const template = '88px repeat(4, 1fr) 124px 184px';
const titles = [
  'Date',
  'Expense itemized',
  'Event name',
  'Rep',
  'Amount',
  'Status',
];
const cellsMobile = [
  { row: '1/2', col: '1/2' },
  { row: '1/2', col: '2/3' },
  { row: '2/3', col: '1/2' },
  { row: '2/3', col: '2/3' },
  { row: '3/4', col: '1/3' },
  { row: '5/6', col: '1/3' },
  { row: '6/7', col: '1/3' },
];

const CommonList = ({
  data,
  eventId,
  isAdvances,
  mapQueryProps,
}: CommonListProps) => {
  const mobile = isMobile();
  const authUser = useAppSelector(selectAuthUser);
  const isCompanyOwner = useAppSelector(isAuthCompanyUser);
  const { debounceQuery, ...searchProps } = useSearchQuery();
  const filteredData = useFilter<Payout>({
    data: data || [],
    query: debounceQuery,
    paths: ['event.name', 'amount', 'employee.user.name', 'date'],
  });

  const title = isAdvances ? 'advance' : 'bonus';
  const canCreateBonus = authUser?.features?.includes(Feature.EVENT_MANAGER);

  const [remove, mutation] = useDeleteMutation();
  handleMutation({
    ...mutation,
    successMessage: `${capitalize(title)} ${Messages.DELETED_SUCCESSFULLY}`,
  });

  const { visibleData, ...showMoreProps } = useShowMore(filteredData);

  return (
    <CommonStyles.StyledList
      {...mapQueries(mapQueryProps)}
      {...searchProps}
      {...showMoreProps}
      count={visibleData?.length}
      header={<ListHeader titles={titles} template={template} />}
      searchBarRightSlot={
        canCreateBonus ? (
          <CreateBonusAdvanceDialog eventId={eventId} isAdvances={isAdvances} />
        ) : undefined
      }
    >
      {visibleData?.map((value) => {
        const isShowApprovalPayoutDialog =
          isCompanyOwner && value.status === PayoutStatus.Pending;
        return (
          <ListCard
            padding="16px"
            key={value.id}
            headers={titles}
            template={template}
            cellsMobile={cellsMobile}
            values={[
              formatDate(value?.date),
              value?.type,
              value?.event?.name,
              value?.employee?.user?.name,
              formatPrice(value?.amount),
              <PayoutStatusBadge type={value?.type} status={value?.status} />,
              <Flex
                gap={8}
                justifyContent={
                  isShowApprovalPayoutDialog && mobile
                    ? 'space-between'
                    : 'flex-end'
                }
              >
                {isShowApprovalPayoutDialog && (
                  <ApprovalPayoutDialog data={value} />
                )}

                <DeleteDialog
                  id={value?.id}
                  action={remove}
                  title={`Delete ${title}`}
                  name={`${formatPrice(value?.amount)}`}
                />
              </Flex>,
            ]}
          />
        );
      })}
    </CommonStyles.StyledList>
  );
};

export const Bonuses = ({ event }: BonusesProps) => {
  const { data: bonuses, ...propsBonuses } = useListQuery({
    eventId: String(event.id),
    types: JSON.stringify([PayoutType.Bonus, PayoutType.PrepaidBonus]),
  });

  const { data, ...propsAdvances } = useListQuery({
    eventId: String(event.id),
    types: JSON.stringify([PayoutType.Advance, PayoutType.PrepaidAdvance]),
  });

  const commonProps = {
    eventId: event.id,
    mapQueryProps: [propsBonuses, propsAdvances],
  };

  return (
    <Flex gap={24} flexDirection="column">
      <CommonList {...commonProps} data={bonuses} />
      <CommonList {...commonProps} isAdvances data={data} />
    </Flex>
  );
};
